import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Child,
  MedicalData,
  MedicalHistory,
  createMedicalHistory,
  fetchChildren,
  fetchMedicalData,
  fetchMedicalHistories
} from '../api/client';
import { useAuthStore } from '../stores/auth';

export function AddMedicalHistory() {
  const { t, i18n } = useTranslation(['common', 'medicalHistory']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [success, setSuccess] = useState(false);
  const [children, setChildren] = useState<Child[]>([]);
  const [filteredChildren, setFilteredChildren] = useState<Child[]>([]);
  const [medicalHistories, setMedicalHistories] = useState<MedicalHistory[]>([]);
  const [medicalData, setMedicalData] = useState<MedicalData | null>(null);
  const { token, parent } = useAuthStore();
  const currentYear = new Date().getFullYear();
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Effect to handle clicks outside the modal
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setShowTermsModal(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef]);

  // Form state
  const [formData, setFormData] = useState({
    child: 0,
    year: currentYear,
    weight: "",
    blood_type: "",
    
    // Vaccination information
    is_vaccinated: true,
    missing_vaccines: [] as number[],
    other_missing_vaccines: "",
    
    // Known diseases
    has_any_known_disease: false,
    known_diseases: [] as number[],
    other_known_diseases: "",
    
    // Allergies
    has_allergies: false,
    allergies: [] as number[],
    other_allergies: "",
    
    // Major disease
    has_any_major_disease: false,
    major_disease: "",
    
    // Psychological disorders
    has_psychological_disorder: false,
    psychological_disorders: [] as number[],
    other_psychological_disorders: "",
    
    // Medication
    is_receiving_medication: false,
    medication: "",
    
    // Development disorders
    has_any_development_disorder: false,
    development_disorders: [] as number[],
    other_development_disorders: "",
    
    // Specialist
    is_being_watched_by_specialist: false,
    specialist_contact_info: "",
    
    // Health issues
    has_worth_mentioning_health_issues: false,
    worth_mentioning_health_issues: "",
    
    // Risk group
    belongs_to_risk_group: false,
    risk_group_explanation: "",
    
    // Underlying diseases
    has_underlying_disease: false,
    underlying_diseases: [] as number[],
    other_underlying_diseases: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !parent?.id) {
        setLoading(false);
        return;
      }

      try {
        const [childrenData, medicalDataResponse, medicalHistoriesData] = await Promise.all([
          fetchChildren(),
          fetchMedicalData(),
          fetchMedicalHistories()
        ]);
        setChildren(childrenData || []);
        setMedicalData(medicalDataResponse || null);
        setMedicalHistories(medicalHistoriesData || []);
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'common' }));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, parent, t]);

  // Filter children who already have medical history for the current year
  useEffect(() => {
    if (children.length && medicalHistories.length) {
      // Get IDs of children who already have medical history for current year
      const childrenWithHistory = new Set(
        medicalHistories
          .filter(history => history.year === currentYear)
          .map(history => history.child)
      );

      // Filter out children who already have medical history
      const availableChildren = children.filter(child => !childrenWithHistory.has(child.id));
      
      setFilteredChildren(availableChildren);
    } else {
      setFilteredChildren(children);
    }
  }, [children, medicalHistories, currentYear]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Handle checkbox inputs
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));

      // Reset related fields when a toggle is turned off
      if (!checked) {
        switch (name) {
          case 'is_vaccinated':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              missing_vaccines: [],
              other_missing_vaccines: ""
            }));
            break;
          case 'has_any_known_disease':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              known_diseases: [],
              other_known_diseases: ""
            }));
            break;
          case 'has_allergies':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              allergies: [],
              other_allergies: ""
            }));
            break;
          case 'has_any_major_disease':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              major_disease: ""
            }));
            break;
          case 'has_psychological_disorder':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              psychological_disorders: [],
              other_psychological_disorders: ""
            }));
            break;
          case 'is_receiving_medication':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              medication: ""
            }));
            break;
          case 'has_any_development_disorder':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              development_disorders: [],
              other_development_disorders: ""
            }));
            break;
          case 'is_being_watched_by_specialist':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              specialist_contact_info: ""
            }));
            break;
          case 'has_worth_mentioning_health_issues':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              worth_mentioning_health_issues: ""
            }));
            break;
          case 'belongs_to_risk_group':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              risk_group_explanation: ""
            }));
            break;
          case 'has_underlying_disease':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              underlying_diseases: [],
              other_underlying_diseases: ""
            }));
            break;
          default:
            break;
        }
      }
      return;
    }
    
    // Handle normal inputs
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (field: string, id: number, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field as keyof typeof prev] as number[], id]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: (prev[field as keyof typeof prev] as number[]).filter(item => item !== id)
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setFieldErrors({});

    if (!termsAccepted) {
      setError(t('errors.termsRequired', { ns: 'common' }));
      setSubmitting(false);
      return;
    }
    
    // Validation checks
    const newFieldErrors: {[key: string]: string} = {};
    let hasErrors = false;
    
    // Basic validation
    if (!formData.child) {
      newFieldErrors.child = t('validation.childRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (!formData.weight) {
      newFieldErrors.weight = t('validation.weightRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    // Convert weight from string to number
    const weightNum = parseFloat(formData.weight as string);
    if (formData.weight && isNaN(weightNum)) {
      newFieldErrors.weight = t('validation.weightInvalid', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    // Additional validation based on conditionals
    if (!formData.is_vaccinated && 
        formData.missing_vaccines.length === 0 && 
        !formData.other_missing_vaccines) {
      newFieldErrors.missing_vaccines = t('validation.missingVaccinesRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (formData.has_any_known_disease && 
        formData.known_diseases.length === 0 && 
        !formData.other_known_diseases) {
      newFieldErrors.known_diseases = t('validation.knownDiseasesRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (formData.has_allergies && 
        formData.allergies.length === 0 && 
        !formData.other_allergies) {
      newFieldErrors.allergies = t('validation.allergiesRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (formData.has_any_major_disease && 
        !formData.major_disease) {
      newFieldErrors.major_disease = t('validation.majorDiseaseRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (formData.has_psychological_disorder && 
        formData.psychological_disorders.length === 0 && 
        !formData.other_psychological_disorders) {
      newFieldErrors.psychological_disorders = t('validation.psychologicalDisordersRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (formData.is_receiving_medication && 
        !formData.medication) {
      newFieldErrors.medication = t('validation.medicationRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (formData.has_any_development_disorder && 
        formData.development_disorders.length === 0 && 
        !formData.other_development_disorders) {
      newFieldErrors.development_disorders = t('validation.developmentDisordersRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (formData.is_being_watched_by_specialist && 
        !formData.specialist_contact_info) {
      newFieldErrors.specialist_contact_info = t('validation.specialistContactRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (formData.has_worth_mentioning_health_issues && 
        !formData.worth_mentioning_health_issues) {
      newFieldErrors.worth_mentioning_health_issues = t('validation.healthIssuesRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (formData.belongs_to_risk_group && 
        !formData.risk_group_explanation) {
      newFieldErrors.risk_group_explanation = t('validation.riskGroupRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (formData.has_underlying_disease && 
        formData.underlying_diseases.length === 0 && 
        !formData.other_underlying_diseases) {
      newFieldErrors.underlying_diseases = t('validation.underlyingDiseasesRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }
    
    if (hasErrors) {
      setFieldErrors(newFieldErrors);
      setSubmitting(false);

      // Scroll to the first error
      const firstErrorField = Object.keys(newFieldErrors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }
    
    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        weight: weightNum,
      };
      
      // Submit data
      await createMedicalHistory(submitData);
      
      // Redirect to homepage
      navigate('/');
      
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('addPage.error', { ns: 'medicalHistory' }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neobrutalism-accent"></div>
      </div>
    );
  }

  if (filteredChildren.length === 0 && !loading) {
    return (
      <div className="card-neobrutalism h-full flex flex-col">
        <h2 className="text-2xl font-black text-neobrutalism-text mb-4">
          {t('addPage.title', { ns: 'medicalHistory' })}
        </h2>
        
        <div className="bg-yellow-50 border-2 border-yellow-300 p-4 rounded-lg mb-4">
          <h3 className="text-lg font-bold mb-2">
            {children.length === 0 
              ? t('addPage.noChildren.title', { ns: 'medicalHistory' })
              : t('addPage.allChildrenHaveHistory', { ns: 'medicalHistory' })}
          </h3>
          <p>
            {children.length === 0 
              ? t('addPage.noChildren.message', { ns: 'medicalHistory' })
              : t('addPage.allChildrenHaveHistoryMessage', { ns: 'medicalHistory', year: currentYear })}
          </p>
          <div className="mt-4 flex sm:justify-end">
            <button
              onClick={() => navigate('/')}
              className="btn-neobrutalism bg-yellow-400 text-center py-2 px-4 mr-2"
            >
              {t('returnButton', { ns: 'medicalHistory' })}
            </button>
            <button
              onClick={() => navigate('/children/add')}
              className="btn-neobrutalism bg-neobrutalism-accent text-center py-2 px-4"
            >
              {t('addPage.noChildren.addChildButton', { ns: 'medicalHistory' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-neobrutalism h-full flex flex-col">
      <h2 className="text-2xl font-black text-neobrutalism-text mb-4">
        {t('addPage.title', { ns: 'medicalHistory' })}
      </h2>
      
      <p className="mb-4 text-gray-600">{t('addExplanation', { ns: 'medicalHistory' })}</p>
      
      {error && (
        <div className="bg-red-50 border-2 border-red-300 p-4 rounded-lg mb-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border-2 border-green-300 p-4 rounded-lg mb-4">
          <p className="text-green-700">{t('addPage.success', { ns: 'medicalHistory' })}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className={`space-y-6 ${submitting ? 'opacity-60 pointer-events-none' : ''}`}>
        {submitting && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neobrutalism-accent"></div>
          </div>
        )}
        {/* Basic Information */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.basicInformation', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label htmlFor="child" className="block mb-2 font-semibold">
              {t('form.child', { ns: 'medicalHistory' })} *
            </label>
            <select
              id="child"
              name="child"
              value={formData.child}
              onChange={handleInputChange}
              className="w-full p-2 border-2 border-black"
            >
              <option value="">{t('form.selectChild', { ns: 'medicalHistory' })}</option>
              {filteredChildren.map(child => (
                <option key={child.id} value={child.id}>
                  {child.first_name} {child.last_name}
                </option>
              ))}
            </select>
            {fieldErrors['child'] && (
              <p className="text-neobrutalism-red mt-1 font-bold">{fieldErrors['child']}</p>
            )}
          </div>
          
          <div className="mb-4">
            <label htmlFor="weight" className="block mb-2 font-semibold">
              {t('form.weight', { ns: 'medicalHistory' })} *
            </label>
            <input
              id="weight"
              name="weight"
              type="number"
              step="0.01"
              min="0"
              max="500"
              value={formData.weight}
              onChange={handleInputChange}
              className="w-full p-2 border-2 border-black"
            />
            {fieldErrors['weight'] && (
              <p className="text-neobrutalism-red mt-1 font-bold">{fieldErrors['weight']}</p>
            )}
          </div>
          
          <div className="mb-4">
            <label htmlFor="blood_type" className="block mb-2 font-semibold">
              {t('form.bloodType', { ns: 'medicalHistory' })}
            </label>
            <select
              id="blood_type"
              name="blood_type"
              value={formData.blood_type}
              onChange={handleInputChange}
              className="w-full p-2 border-2 border-black"
            >
              <option value="">{t('form.selectBloodType', { ns: 'medicalHistory' })}</option>
              {/* Always use standard English blood types - never translate these medical values */}
              {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Vaccination Information */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.vaccination', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="is_vaccinated"
                checked={formData.is_vaccinated}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.isVaccinated', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {!formData.is_vaccinated && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="missing_vaccines" className="block mb-2 font-semibold">
                  {t('missingVaccinesLabel', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.missing_vaccines?.map(vaccine => (
                    <label key={vaccine.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.missing_vaccines.includes(vaccine.id)}
                        onChange={(e) => handleMultiSelectChange('missing_vaccines', vaccine.id, e.target.checked)}
                        className="h-5 w-5 mr-2"
                      />
                      <span>{vaccine.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.missing_vaccines && (
                  <p className="text-neobrutalism-red mt-1 mb-2 font-bold">{fieldErrors.missing_vaccines}</p>
                )}
                
                <div>
                  <label htmlFor="other_missing_vaccines" className="block mb-2 font-semibold">
                    {t('form.otherMissingVaccines', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_missing_vaccines"
                    name="other_missing_vaccines"
                    type="text"
                    value={formData.other_missing_vaccines}
                    onChange={handleInputChange}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('form.otherMissingVaccinesPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Known Diseases */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.knownDiseases', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_any_known_disease"
                checked={formData.has_any_known_disease}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.hasKnownDisease', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_any_known_disease && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="known_diseases" className="block mb-2 font-semibold">
                  {t('form.knownDiseases', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.known_diseases?.map(disease => (
                    <label key={disease.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.known_diseases.includes(disease.id)}
                        onChange={(e) => handleMultiSelectChange('known_diseases', disease.id, e.target.checked)}
                        className="h-5 w-5 mr-2"
                      />
                      <span>{disease.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.known_diseases && (
                  <p className="text-neobrutalism-red mt-1 mb-2 font-bold">{fieldErrors.known_diseases}</p>
                )}
                
                <div>
                  <label htmlFor="other_known_diseases" className="block mb-2 font-semibold">
                    {t('form.otherKnownDiseases', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_known_diseases"
                    name="other_known_diseases"
                    type="text"
                    value={formData.other_known_diseases}
                    onChange={handleInputChange}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('form.otherKnownDiseasesPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Allergies */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.allergies', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_allergies"
                checked={formData.has_allergies}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.hasAllergies', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_allergies && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="allergies" className="block mb-2 font-semibold">
                  {t('form.allergies', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.allergies?.map(allergy => (
                    <label key={allergy.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allergies.includes(allergy.id)}
                        onChange={(e) => handleMultiSelectChange('allergies', allergy.id, e.target.checked)}
                        className="h-5 w-5 mr-2"
                      />
                      <span>{allergy.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.allergies && (
                  <p className="text-neobrutalism-red mt-1 mb-2 font-bold">{fieldErrors.allergies}</p>
                )}
                
                <div>
                  <label htmlFor="other_allergies" className="block mb-2 font-semibold">
                    {t('form.otherAllergies', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_allergies"
                    name="other_allergies"
                    type="text"
                    value={formData.other_allergies}
                    onChange={handleInputChange}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('form.otherAllergiesPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Major Disease */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.majorDisease', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_any_major_disease"
                checked={formData.has_any_major_disease}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.hasMajorDisease', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_any_major_disease && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label htmlFor="major_disease" className="block mb-2 font-semibold">
                  {t('form.majorDisease', { ns: 'medicalHistory' })} *
                </label>
                <input
                  id="major_disease"
                  name="major_disease"
                  type="text"
                  value={formData.major_disease}
                  onChange={handleInputChange}
                  className="w-full p-2 border-2 border-black"
                  placeholder={t('form.majorDiseasePlaceholder', { ns: 'medicalHistory' })}
                />
                {fieldErrors.major_disease && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{fieldErrors.major_disease}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Psychological Disorders */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.psychologicalDisorders', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_psychological_disorder"
                checked={formData.has_psychological_disorder}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.hasPsychologicalDisorder', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_psychological_disorder && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="psychological_disorders" className="block mb-2 font-semibold">
                  {t('form.psychologicalDisorders', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.psychological_disorders?.map(disorder => (
                    <label key={disorder.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.psychological_disorders.includes(disorder.id)}
                        onChange={(e) => handleMultiSelectChange('psychological_disorders', disorder.id, e.target.checked)}
                        className="h-5 w-5 mr-2"
                      />
                      <span>{disorder.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.psychological_disorders && (
                  <p className="text-neobrutalism-red mt-1 mb-2 font-bold">{fieldErrors.psychological_disorders}</p>
                )}
                
                <div>
                  <label htmlFor="other_psychological_disorders" className="block mb-2 font-semibold">
                    {t('form.otherPsychologicalDisorders', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_psychological_disorders"
                    name="other_psychological_disorders"
                    type="text"
                    value={formData.other_psychological_disorders}
                    onChange={handleInputChange}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('form.otherPsychologicalDisordersPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Medication */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.medication', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="is_receiving_medication"
                checked={formData.is_receiving_medication}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.isReceivingMedication', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.is_receiving_medication && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label htmlFor="medication" className="block mb-2 font-semibold">
                  {t('form.medication', { ns: 'medicalHistory' })} *
                </label>
                <input
                  id="medication"
                  name="medication"
                  type="text"
                  value={formData.medication}
                  onChange={handleInputChange}
                  className="w-full p-2 border-2 border-black"
                  placeholder={t('form.medicationPlaceholder', { ns: 'medicalHistory' })}
                />
                {fieldErrors.medication && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{fieldErrors.medication}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Development Disorders */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.developmentDisorders', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_any_development_disorder"
                checked={formData.has_any_development_disorder}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.hasDevelopmentDisorder', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_any_development_disorder && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="development_disorders" className="block mb-2 font-semibold">
                  {t('form.developmentDisorders', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.development_disorders?.map(disorder => (
                    <label key={disorder.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.development_disorders.includes(disorder.id)}
                        onChange={(e) => handleMultiSelectChange('development_disorders', disorder.id, e.target.checked)}
                        className="h-5 w-5 mr-2"
                      />
                      <span>{disorder.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.development_disorders && (
                  <p className="text-neobrutalism-red mt-1 mb-2 font-bold">{fieldErrors.development_disorders}</p>
                )}
                
                <div>
                  <label htmlFor="other_development_disorders" className="block mb-2 font-semibold">
                    {t('form.otherDevelopmentDisorders', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_development_disorders"
                    name="other_development_disorders"
                    type="text"
                    value={formData.other_development_disorders}
                    onChange={handleInputChange}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('form.otherDevelopmentDisordersPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Specialist */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.specialistCare', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="is_being_watched_by_specialist"
                checked={formData.is_being_watched_by_specialist}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.isWatchedBySpecialist', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.is_being_watched_by_specialist && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label htmlFor="specialist_contact_info" className="block mb-2 font-semibold">
                  {t('form.specialistContactInfo', { ns: 'medicalHistory' })} *
                </label>
                <input
                  id="specialist_contact_info"
                  name="specialist_contact_info"
                  type="text"
                  value={formData.specialist_contact_info}
                  onChange={handleInputChange}
                  className="w-full p-2 border-2 border-black"
                  placeholder={t('form.specialistContactInfoPlaceholder', { ns: 'medicalHistory' })}
                />
                {fieldErrors.specialist_contact_info && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{fieldErrors.specialist_contact_info}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Health Issues */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.healthIssues', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_worth_mentioning_health_issues"
                checked={formData.has_worth_mentioning_health_issues}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.hasHealthIssues', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_worth_mentioning_health_issues && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label htmlFor="worth_mentioning_health_issues" className="block mb-2 font-semibold">
                  {t('form.healthIssues', { ns: 'medicalHistory' })} *
                </label>
                <textarea
                  id="worth_mentioning_health_issues"
                  name="worth_mentioning_health_issues"
                  value={formData.worth_mentioning_health_issues}
                  onChange={handleInputChange}
                  className="w-full p-2 border-2 border-black"
                  rows={3}
                  placeholder={t('form.healthIssuesPlaceholder', { ns: 'medicalHistory' })}
                />
                {fieldErrors.worth_mentioning_health_issues && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{fieldErrors.worth_mentioning_health_issues}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Risk Group */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.riskGroup', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="belongs_to_risk_group"
                checked={formData.belongs_to_risk_group}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.belongsToRiskGroup', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.belongs_to_risk_group && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label htmlFor="risk_group_explanation" className="block mb-2 font-semibold">
                  {t('form.riskGroupExplanation', { ns: 'medicalHistory' })} *
                </label>
                <textarea
                  id="risk_group_explanation"
                  name="risk_group_explanation"
                  value={formData.risk_group_explanation}
                  onChange={handleInputChange}
                  className="w-full p-2 border-2 border-black"
                  rows={3}
                  placeholder={t('form.riskGroupExplanationPlaceholder', { ns: 'medicalHistory' })}
                />
                {fieldErrors.risk_group_explanation && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{fieldErrors.risk_group_explanation}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Underlying Diseases */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-100">
          <h3 className="text-lg font-bold mb-4">{t('sections.underlyingDiseases', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_underlying_disease"
                checked={formData.has_underlying_disease}
                onChange={handleInputChange}
                className="h-5 w-5 mr-2"
              />
              <span>{t('form.hasUnderlyingDisease', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_underlying_disease && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="underlying_diseases" className="block mb-2 font-semibold">
                  {t('form.underlyingDiseases', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.underlying_diseases?.map(disease => (
                    <label key={disease.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.underlying_diseases.includes(disease.id)}
                        onChange={(e) => handleMultiSelectChange('underlying_diseases', disease.id, e.target.checked)}
                        className="h-5 w-5 mr-2"
                      />
                      <span>{disease.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.underlying_diseases && (
                  <p className="text-neobrutalism-red mt-1 mb-2 font-bold">{fieldErrors.underlying_diseases}</p>
                )}
                
                <div>
                  <label htmlFor="other_underlying_diseases" className="block mb-2 font-semibold">
                    {t('form.otherUnderlyingDiseases', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_underlying_diseases"
                    name="other_underlying_diseases"
                    type="text"
                    value={formData.other_underlying_diseases}
                    onChange={handleInputChange}
                    className="w-full p-2 border-2 border-black"
                    placeholder={t('form.otherUnderlyingDiseasesPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Terms and Conditions Checkbox */}
        <div className="flex items-start items-center mt-6">
          <div className="flex items-center h-5">
            <input
              id="termsAccepted"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="w-5 h-5 border-2 border-black focus:ring-neobrutalism-accent"
              required
            />
          </div>
          <div className="ml-3 text-base">
            <label htmlFor="termsAccepted" className="font-medium text-gray-700">
              {t('form.termsAndConditionsLabel', { ns: 'medicalHistory' })}
            </label>
            <p className="text-gray-600">
              {t('form.termsAndConditionsFullTextPart1', { ns: 'medicalHistory' })}{' '}
              <button
                type="button"
                onClick={() => setShowTermsModal(true)}
                className="text-blue-600 hover:underline focus:outline-none font-medium"
              >
                {t('form.termsAndConditionsLink', { ns: 'medicalHistory' })}
              </button>
              {' '}{t('form.termsAndConditionsFullTextPart2', { ns: 'medicalHistory' })}
            </p>
          </div>
        </div>
        
        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-end mt-6">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-neobrutalism bg-neobrutalism-red py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base"
          >
            {t('cancel', { ns: 'common' })}
          </button>
          
          <button
            type="submit"
            disabled={submitting || !termsAccepted}
            className={`btn-neobrutalism py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base ${
              termsAccepted && !submitting ? 'bg-neobrutalism-accent' : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? t('submitting', { ns: 'common' }) : t('submitButton', { ns: 'medicalHistory' })}
          </button>
        </div>
      </form>

      {showTermsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full flex items-center justify-center z-50 px-4">
          <div ref={modalRef} className="relative mx-auto p-6 border w-full max-w-2xl shadow-xl rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl leading-6 font-bold text-gray-900">{t('form.termsAndConditionsTitle', { ns: 'medicalHistory' })}</h3>
              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                onClick={() => setShowTermsModal(false)}
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mt-2 text-left text-sm text-gray-600 max-h-[70vh] overflow-y-auto pr-2">
              <p className="whitespace-pre-line">
                {t('form.termsAndConditionsContent', { ns: 'medicalHistory' })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 